// src/store/slices/healthCoachSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { healthCoach } from "../../types/index";

// Initial state for the health coach slice
const initialState: healthCoach.HealthCoachStateType = {
  healthCoachDetails: {
    isLoading: false,
    data: null,
    errors: null,
  },
  scheduleCycleState: {
    isLoading: false,
    data: null,
    errors: null,
  },
  updateVisitDetailsState: {
    isLoading: false,
    data: null,
    errors: null,
  },
  bulkScheduleVisitDetailsState: {
    isLoading: false,
    data: null,
    errors: null,
  },
  patientVisitDetailsState: {
    isLoading: false,
    data: null,
    errors: null,
  },
  bulkUploadPatientRecordsState: {
    isLoading: false,
    data: null,
    errors: null,
  },
};

// Define the health coach slice
const healthCoachSlice = createSlice({
  name: "healthCoach",
  initialState,
  reducers: {
    getHealthCoachDetailsRequest: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.healthCoachDetails.isLoading = true;
      state.healthCoachDetails.data = null;
      state.healthCoachDetails.errors = "";
    },
    getHealthCoachDetailsSuccess: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.healthCoachDetails.isLoading = false;
      state.healthCoachDetails.data = action.payload.data;
      state.healthCoachDetails.errors = null;
    },
    getHealthCoachDetailsError: (
      state: healthCoach.HealthCoachStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.healthCoachDetails.isLoading = false;
      state.healthCoachDetails.data = null;
      state.healthCoachDetails.errors = error;
    },
    resetHealthCoachDetails: (state: healthCoach.HealthCoachStateType) => {
      state.healthCoachDetails.isLoading = false;
      state.healthCoachDetails.data = null;
      state.healthCoachDetails.errors = null;
    },

    // Schedule Cycle
    scheduleCycleRequest: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
      isForBulkProcess?: boolean,
    ) => {
      state.scheduleCycleState.isLoading = true;
      state.scheduleCycleState.data = null;
      state.scheduleCycleState.errors = null;
    },
    scheduleCycleSuccess: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.scheduleCycleState.isLoading = false;
      state.scheduleCycleState.data = action.payload.data;
      state.scheduleCycleState.errors = null;
    },
    scheduleCycleError: (
      state: healthCoach.HealthCoachStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.scheduleCycleState.isLoading = false;
      state.scheduleCycleState.data = null;
      state.scheduleCycleState.errors = error;
    },
    resetScheduleCycle: (state: healthCoach.HealthCoachStateType) => {
      state.scheduleCycleState.isLoading = false;
      state.scheduleCycleState.data = null;
      state.scheduleCycleState.errors = null;
    },

    // Update Visit Details
    updateVisitDetailsRequest: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.updateVisitDetailsState.isLoading = true;
      state.updateVisitDetailsState.data = null;
      state.updateVisitDetailsState.errors = null;
    },
    updateVisitDetailsSuccess: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.updateVisitDetailsState.isLoading = false;
      state.updateVisitDetailsState.data = action.payload.data;
      state.updateVisitDetailsState.errors = null;
    },
    updateVisitDetailsError: (
      state: healthCoach.HealthCoachStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.updateVisitDetailsState.isLoading = false;
      state.updateVisitDetailsState.data = null;
      state.updateVisitDetailsState.errors = error;
    },
    resetUpdateVisitDetails: (state: healthCoach.HealthCoachStateType) => {
      state.updateVisitDetailsState.isLoading = false;
      state.updateVisitDetailsState.data = null;
      state.updateVisitDetailsState.errors = null;
    },

    // Bulk Schedule Visit Details
    bulkScheduleVisitDetailsRequest: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.bulkScheduleVisitDetailsState.isLoading = true;
      state.bulkScheduleVisitDetailsState.data = null;
      state.bulkScheduleVisitDetailsState.errors = null;
    },
    bulkScheduleVisitDetailsSuccess: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.bulkScheduleVisitDetailsState.isLoading = false;
      state.bulkScheduleVisitDetailsState.data = action.payload;
      state.bulkScheduleVisitDetailsState.errors = null;
    },
    bulkScheduleVisitDetailsError: (
      state: healthCoach.HealthCoachStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.bulkScheduleVisitDetailsState.isLoading = false;
      state.bulkScheduleVisitDetailsState.data = null;
      state.bulkScheduleVisitDetailsState.errors = error;
    },
    resetBulkScheduleVisitDetails: (
      state: healthCoach.HealthCoachStateType,
    ) => {
      state.bulkScheduleVisitDetailsState.isLoading = false;
      state.bulkScheduleVisitDetailsState.data = null;
      state.bulkScheduleVisitDetailsState.errors = null;
    },

    // Get Patient Visit Details
    getPatientVisitDetailsRequest: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.patientVisitDetailsState.isLoading = true;
      state.patientVisitDetailsState.data = null;
      state.patientVisitDetailsState.errors = null;
    },
    getPatientVisitDetailsSuccess: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.patientVisitDetailsState.isLoading = false;
      state.patientVisitDetailsState.data = action.payload.data;
      state.patientVisitDetailsState.errors = null;
    },
    getPatientVisitDetailsError: (
      state: healthCoach.HealthCoachStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.patientVisitDetailsState.isLoading = false;
      state.patientVisitDetailsState.data = null;
      state.patientVisitDetailsState.errors = error;
    },
    resetGetPatientVisitDetails: (state: healthCoach.HealthCoachStateType) => {
      state.patientVisitDetailsState.isLoading = false;
      state.patientVisitDetailsState.data = null;
      state.patientVisitDetailsState.errors = null;
    },

    // Bulk Upload Patient Records
    bulkUploadPatientRecordsRequest: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<{
        formData: FormData;
        silent?: boolean;
        resolve?: (value?: unknown) => void;
        reject?: (reason?: unknown) => void;
      }>,
    ) => {
      state.bulkUploadPatientRecordsState.isLoading = true;
      state.bulkUploadPatientRecordsState.data = null;
      state.bulkUploadPatientRecordsState.errors = null;
    },
    bulkUploadPatientRecordsSuccess: (
      state: healthCoach.HealthCoachStateType,
      action: PayloadAction<any>,
    ) => {
      state.bulkUploadPatientRecordsState.isLoading = false;
      state.bulkUploadPatientRecordsState.data = action.payload.data;
      state.bulkUploadPatientRecordsState.errors = null;
    },
    bulkUploadPatientRecordsError: (
      state: healthCoach.HealthCoachStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.bulkUploadPatientRecordsState.isLoading = false;
      state.bulkUploadPatientRecordsState.data = null;
      state.bulkUploadPatientRecordsState.errors = error;
    },
    resetBulkUploadPatientRecords: (
      state: healthCoach.HealthCoachStateType,
    ) => {
      state.bulkUploadPatientRecordsState.isLoading = false;
      state.bulkUploadPatientRecordsState.data = null;
      state.bulkUploadPatientRecordsState.errors = null;
    },
  },
});

// Export actions and reducer
export const {
  getHealthCoachDetailsRequest,
  getHealthCoachDetailsSuccess,
  getHealthCoachDetailsError,
  resetHealthCoachDetails,

  // Schedule Cycle
  scheduleCycleRequest,
  scheduleCycleSuccess,
  scheduleCycleError,
  resetScheduleCycle,

  // Update Visit Details
  updateVisitDetailsRequest,
  updateVisitDetailsSuccess,
  updateVisitDetailsError,
  resetUpdateVisitDetails,

  // Bulk Schedule Visit Details
  bulkScheduleVisitDetailsRequest,
  bulkScheduleVisitDetailsSuccess,
  bulkScheduleVisitDetailsError,
  resetBulkScheduleVisitDetails,

  // Get Patient Visit Details
  getPatientVisitDetailsRequest,
  getPatientVisitDetailsSuccess,
  getPatientVisitDetailsError,
  resetGetPatientVisitDetails,

  // Bulk Upload Patient Records
  bulkUploadPatientRecordsRequest,
  bulkUploadPatientRecordsSuccess,
  bulkUploadPatientRecordsError,
  resetBulkUploadPatientRecords,
} = healthCoachSlice.actions;

export default healthCoachSlice.reducer;
